import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";
import { sendDocumentForSign } from "../../api/documentsApi";
import { getArtists } from "../../api/artistApi";
import { getPromoters } from "../../api/promotersApi";
import TagMultiSelect from "../../components/multiSelect";

const SendForSignModal = ({ isOpen, onClose, documentId, onSuccess }) => {
  const [artists, setArtists] = useState([]);
  const [promoters, setPromoters] = useState([]);

  const [selectedArtists, setSelectedArtists] = useState([]);
  const [selectedPromoters, setSelectedPromoters] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const resetForm = () => {
    setSelectedArtists([]);
    setSelectedPromoters([]);
    setError("");
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const payload = {
        artists: selectedArtists.map((a) => a.id),
        promoters: selectedPromoters.map((p) => p.id),
      };

      await sendDocumentForSign(documentId, payload);

      onSuccess();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Failed to send document for signing.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      try {
        const [artistsRes, promotersRes] = await Promise.all([
          getArtists(),
          getPromoters(),
        ]);

        const formattedArtists = (artistsRes.results || artistsRes).map(
          (artist) => ({
            id: artist.id,
            name: artist.real_name,
          }),
        );

        const formattedPromoters = (promotersRes.results || promotersRes).map(
          (promoter) => ({
            id: promoter.id,
            name: promoter.name,
          }),
        );

        setArtists(formattedArtists);
        setPromoters(formattedPromoters);
      } catch (err) {
        setError("Failed to load artists/promoters.");
      }
    };

    loadData();
  }, [isOpen]);

  useEffect(() => {
  if (isOpen) {
    setSelectedArtists([]);
    setSelectedPromoters([]);
    setError("");
  }
}, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            Send Document For Signature
          </h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <TagMultiSelect
            label="Select Artists"
            options={artists}
            selected={selectedArtists}
            setSelected={setSelectedArtists}
          />

          <TagMultiSelect
            label="Select Promoters"
            options={promoters}
            selected={selectedPromoters}
            setSelected={setSelectedPromoters}
          />

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-2 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium ${
              loading
                ? "bg-pink-400 cursor-not-allowed"
                : "bg-pink-600 hover:bg-pink-700"
            }`}
          >
            {loading ? "Sending..." : "Send For Signature"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendForSignModal;
